/**
 * ============================================================================
 * CustomerTable - Main Customers Table Component
 * ============================================================================
 */

import React, { useEffect, useState } from 'react';
import {
  Search,
  User,
  Mail,
  Phone,
  Calendar,
  TrendingUp,
  Smartphone,
  UserPlus,
  Crown,
  ShieldCheck,
} from 'lucide-react';
import type { TrustedEntry } from '../../pages/CustomerInsightsNew';
import {
  formatCurrency,
  formatDate,
  getTypeInfo,
} from '../../utils/customerInsightsFormatters';
import type { Customer, CustomerInsightsFilters } from '../../types/customerInsights';

interface CustomerTableProps {
  customers: Customer[];
  filters: CustomerInsightsFilters;
  onFilterChange: (filters: Partial<CustomerInsightsFilters>) => void;
  onCustomerClick?: (customer: Customer) => void;
  onCustomerHover?: (customer: Customer) => void;
  // Trusted customer badge (read-only — actions are in CustomerProfileModal)
  trustedByUserId?: Map<number, TrustedEntry>;
}

export function CustomerTable({
  customers,
  filters,
  onFilterChange,
  onCustomerClick,
  onCustomerHover,
  trustedByUserId,
}: CustomerTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Reset to page 1 when customers list or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [customers.length, filters.search, filters.segment, filters.type]);

  // Pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCustomers = customers.slice(startIndex, endIndex);
  const totalPages = Math.ceil(customers.length / itemsPerPage);

  return (
    <div>
      {/* Filters Bar */}
      <div className="bg-white rounded-xl border border-gray-200 mb-4">
        <div className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1 overflow-x-auto">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={filters.search}
                onChange={(e) => onFilterChange({ search: e.target.value })}
                className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-500 w-full text-sm"
                aria-label="Search customers"
              />
            </div>

            {/* Type Filter */}
            <select
              value={filters.type}
              onChange={(e) => onFilterChange({ type: e.target.value as any })}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 outline-none focus:border-blue-500 whitespace-nowrap"
              aria-label="Filter by type"
            >
              <option value="all">All Types</option>
              <option value="new">New Customers</option>
              <option value="returning">Returning Customers</option>
            </select>

            {/* Members Only Filter */}
            <select
              value={filters.segment === 'member' ? 'member' : 'all'}
              onChange={(e) => onFilterChange({ segment: e.target.value as any })}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 outline-none focus:border-blue-500 whitespace-nowrap"
              aria-label="Filter by membership"
            >
              <option value="all">All Customers</option>
              <option value="member">Members Only</option>
            </select>
          </div>

          {/* Export Button - Commented out for privacy reasons
          <button
            onClick={handleExport}
            disabled={customers.length === 0}
            className="bg-green-600 text-white px-4 py-2 rounded-xl font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm whitespace-nowrap"
            aria-label="Export customers"
          >
            <Download className="w-4 h-4 mr-2" />
            <span>Export</span>
          </button>
          */}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Customer
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Activity
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Total Spent
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Last Activity
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  Source
                </th>
                {trustedByUserId && (
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                    Trusted
                  </th>
                )}
              </tr>
            </thead>
          <tbody>
            {paginatedCustomers.length === 0 ? (
              <tr>
                <td colSpan={trustedByUserId ? 8 : 7} className="px-4 py-12 text-center text-gray-500">
                  No customers found
                </td>
              </tr>
            ) : (
              paginatedCustomers.map((customer) => (
                <tr
                  key={customer.id}
                  onClick={() => onCustomerClick?.(customer)}
                  onMouseEnter={() => onCustomerHover?.(customer)}
                  className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    onCustomerClick ? 'cursor-pointer' : ''
                  }`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                        customer.has_membership ? 'bg-green-100' : 'bg-blue-100'
                      }`}>
                        {customer.has_membership ? (
                          <Crown className="w-5 h-5 text-green-600" />
                        ) : (
                          <User className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">
                          {customer.name || 'N/A'}
                        </p>
                        {customer.has_membership && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-700">
                            Active Member
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      <div className="flex items-center text-sm text-gray-700">
                        <Mail className="w-3 h-3 mr-2 text-gray-400" />
                        {customer.email || 'N/A'}
                      </div>
                      <div className="flex items-center text-sm text-gray-700">
                        <Phone className="w-3 h-3 mr-2 text-gray-400" />
                        {customer.phone || 'N/A'}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-2">
                        <TrendingUp className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="text-sm">
                        <span className="font-bold text-gray-900">{customer.total_bookings || 0}</span>
                        <span className="text-gray-500"> booking{customer.total_bookings !== 1 ? 's' : ''}</span>
                        {(customer.membership_count ?? 0) > 0 && (
                          <div className="text-xs text-green-600 font-semibold">
                            {customer.membership_count} membership{customer.membership_count !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-black text-green-600">
                      {formatCurrency(customer.total_spent)}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center text-sm text-gray-700">
                      <Calendar className="w-3 h-3 mr-2 text-gray-400" />
                      {formatDate(customer.last_booking_date)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-bold ${
                        getTypeInfo(customer.is_new).colorClass
                      }`}
                    >
                      {getTypeInfo(customer.is_new).label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold ${
                        customer.source === 'spotts'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {customer.source === 'spotts' ? (
                        <Smartphone className="w-3 h-3" />
                      ) : (
                        <UserPlus className="w-3 h-3" />
                      )}
                      {customer.source === 'spotts' ? 'Spotts' : 'Manual'}
                    </span>
                  </td>
                  {/* Trust badge — read-only, click the row to manage */}
                  {trustedByUserId && (() => {
                    const userId = customer.source === 'spotts'
                      ? parseInt(customer.id.replace('spotts_', ''), 10)
                      : null;
                    const isTrusted = userId && !isNaN(userId) ? trustedByUserId.has(userId) : false;

                    return (
                      <td className="px-4 py-3">
                        {isTrusted ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded-lg">
                            <ShieldCheck className="w-3 h-3" />
                            Trusted
                          </span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                    );
                  })()}
                </tr>
              ))
            )}
          </tbody>
          </table>
        </div>

        {/* Pagination */}
        {paginatedCustomers.length > 0 && (
          <div className="px-3 sm:px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
              Showing{' '}
              <span className="font-semibold">
                {startIndex + 1}-{Math.min(endIndex, customers.length)}
              </span>{' '}
              of <span className="font-semibold">{customers.length}</span> customers
            </p>
            <div className="flex items-center justify-center space-x-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-200 rounded-lg text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Previous page"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                    aria-label={`Page ${pageNum}`}
                    aria-current={currentPage === pageNum ? 'page' : undefined}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-200 rounded-lg text-xs sm:text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Next page"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
